import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { loadJobs } from "./loadJobs";
import backgroundImage from "../../assets/HomePage.png";
import "./Career.css";
import { Helmet } from "react-helmet";

const getJobFilterGroup = (job) => {
  if (job.category && job.category !== "Other") {
    return job.category;
  }

  const dept = (job.department || "").trim().toLowerCase();

  if (dept === "it" || dept.startsWith("it (") || dept.startsWith("it(")) {
    return "IT";
  }
  if (dept === "commercial") {
    return "Commercial";
  }
  if (dept === "sales") {
    return "Sales";
  }
  if (dept.includes("finance") || dept.includes("account")) {
    return "Account";
  }
  if (
    dept.includes("digital marketing") ||
    dept === "digital consultant" ||
    dept === "functional consultant"
  ) {
    return "Digital Marketing";
  }

  return "Other";
};

const Career = () => {
  const [jobOpenings, setJobOpenings] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = React.useState("View all");

  useEffect(() => {
    let isMounted = true;

    loadJobs()
      .then((jobs) => {
        if (!isMounted) return;
        setJobOpenings(jobs);
        setFilteredJobs(jobs);
      })
      .catch((err) => {
        console.error("Error loading job openings:", err);
        if (isMounted) setError(err.message);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    if (category === "View all") {
      setFilteredJobs(jobOpenings);
    } else {
      const filtered = jobOpenings.filter(
        (job) => getJobFilterGroup(job) === category
      );

      if (filtered.length === 0) {
        console.warn(`No job openings found for category "${category}"`);
      }

      setFilteredJobs(filtered);
    }
  };

  return (
    <>
      <Helmet>
        <title>Career Opportunities at Navishaa: Join Our Growing Team</title>

        {/* Description */}
        <meta
          name="description"
          content="Explore exciting career opportunities at Navishaa. Whether you're an experienced professional or just starting out, discover how you can contribute to our innovative team and grow with us. Check out our latest job openings and apply today!"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.navishaa.com/career" />

        {/* Open Graph (OG) tags */}
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Career Opportunities at Navishaa: Join Our Growing Team"
        />
        <meta
          property="og:description"
          content="Explore exciting career opportunities at Navishaa. Whether you're an experienced professional or just starting out, discover how you can contribute to our innovative team and grow with us. Check out our latest job openings and apply today!"
        />
        <meta property="og:url" content="https://www.navishaa.com/career" />
        <meta property="og:site_name" content="Navishaa" />
        <meta
          property="og:image"
          content="https://www.navishaa.com/images/career-banner.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="629" />

        {/* Twitter tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@Navishaa" />
        <meta
          name="twitter:title"
          content="Career Opportunities at Navishaa: Join Our Growing Team"
        />
        <meta
          name="twitter:description"
          content="Explore exciting career opportunities at Navishaa. Whether you're an experienced professional or just starting out, discover how you can contribute to our innovative team and grow with us. Check out our latest job openings and apply today!"
        />
        <meta
          name="twitter:image"
          content="https://www.navishaa.com/images/career-banner.png"
        />

        {/* LinkedIn tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.navishaa.com/career" />
        <meta
          property="og:title"
          content="Career Opportunities at Navishaa: Join Our Growing Team"
        />
        <meta
          property="og:description"
          content="Explore exciting career opportunities at Navishaa. Whether you're an experienced professional or just starting out, discover how you can contribute to our innovative team and grow with us. Check out our latest job openings and apply today!"
        />
        <meta
          property="og:image"
          content="https://www.navishaa.com/images/career-banner.png"
        />

        {/* Structured Data - Schema.org */}
        <script type="application/ld+json">
          {`
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Career Opportunities at Navishaa: Join Our Growing Team",
        "author": {
          "@type": "Person",
          "name": "Navishaa"
        },
        "datePublished": "2023-09-05",
        "dateModified": "2023-09-05",
        "image": {
          "@type": "ImageObject",
          "url": "https://www.navishaa.com/images/social-banner.png",
          "width": 1200,
          "height": 629
        },
        "publisher": {
          "@type": "Organization",
          "name": "GetMax",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.navishaa.com/images/logo.png",
            "width": 1854,
            "height": 503
          }
        },
        "mainEntityOfPage": "https://www.navishaa.com/career",
        "url": "https://www.navishaa.com/career",
        "description": "Explore exciting career opportunities at GetMax. Whether you're an experienced professional or just starting out, discover how you can contribute to our innovative team and grow with us. Check out our latest job openings and apply today!"
      }
    `}
        </script>
      </Helmet>
      <div className=" max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-r from-pink-50 to-purple-50">
        {/* Hero Section */}

        {/* Second Section with Background Image */}
        <section
          className="bg-gradient-to-r from-blue-600 to-purple-600 py-12 px-4 text-white relative min-h-screen 
          lg:min-h-[65vh]" // Adjust height for large screens
          style={{
            backgroundImage: `url("https://res.cloudinary.com/dbitupkaa/image/upload/f_auto,q_auto/v1737458572/HomePage_d3y2gy.png")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 opacity-50"></div>{" "}
          {/* Optional overlay to darken the background image */}
          <div className="max-w-7xl mx-auto text-center relative pt-4 md:pt-10 ">
            <span className="bg-white text-blue-700 px-4 py-3  rounded-full inline-block text-sm font-medium shadow-lg mb-4">
              We're hiring!
            </span>
            <h1 className="md:py-4 text-3xl md:text-5xl leading-tight text-black font-bold">
              Be The Part of Our Organization
            </h1>
            <p className="mt-4 text-lg md:text-xl max-w-4xl mx-auto text-gray-800">
              We’re looking for passionate people to join us on our mission. We
              value flat hierarchies, clear communication, and full ownership
              and responsibility.
            </p>
          </div>
          {/* Decorative Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <svg
              className="hidden md:block"
              viewBox="0 0 1024 1024"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path
                d="M512 0c0 282.5-224 512-512 512h1024C736 512 512 282.5 512 0z"
                opacity=".1"
              />
            </svg>
          </div>
        </section>

        {/* Job Listings Section */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4 px-2 md:px-12">
          {[
            "View all",
            "IT",
            "Commercial",
            "Sales",
            "Digital Marketing",
            "Account",
          ].map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryClick(category)}
              className={`px-2 py-2 md:px-4 md:py-3 rounded-full transition duration-300 ease-in-out transform shadow-md
        ${
          activeCategory === category
            ? "bg-blue-600 text-white border border-blue-600"
            : "bg-white text-blue-600 border border-blue-600 hover:bg-blue-600 hover:text-white hover:scale-105"
        }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Job Listings Section */}
        <section className="py-8 px-4 sm:px-6 lg:px-8">
          {error ? (
            <p className="text-red-600 text-center font-semibold">
              Error loading job openings: {error}
            </p>
          ) : isLoading ? (
            <p className="text-center text-gray-700">Loading job openings...</p>
          ) : (
            <div className="max-w-6xl mx-auto space-y-8">
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white p-4 sm:p-6 shadow-lg rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-xl transition-shadow duration-300 ease-in-out"
                  >
                    {/* Job Info Section */}
                    <div className="w-full sm:w-4/5">
                      <Link
                        to={`/job/${job.id}`}
                        className="text-xl md:text-2xl font-semibold text-gray-900 hover:text-blue-600 transition duration-200"
                      >
                        {job.title}
                      </Link>
                      <p className="text-gray-600 mt-2 overflow-hidden max-h-12 line-clamp-1">
                        {job.description}
                      </p>

                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {(job.department ||
                          (job.category !== "Other" ? job.category : null)) && (
                          <span className="text-gray-500 text-xs sm:text-sm bg-gray-100 px-3 py-1 rounded-full">
                            {job.department ||
                              (job.category !== "Other" ? job.category : null)}
                          </span>
                        )}
                        {job.location && (
                          <span className="text-gray-500 text-xs sm:text-sm bg-gray-100 px-3 py-1 rounded-full">
                            {job.location}
                          </span>
                        )}
                        <span className="text-gray-500 text-xs sm:text-sm bg-gray-100 px-3 py-1 rounded-full">
                          Posted on: {job.date}
                        </span>
                      </div>
                    </div>

                    {/* Apply Link Section */}
                    <div className="flex items-center w-full sm:w-1/5 md:justify-end mt-4 sm:mt-0">
                      <Link
                        to={`/job/${job.id}`}
                        className="border border-blue-600 text-blue-600 font-semibold px-4 py-2 rounded-full transition-colors duration-300 hover:bg-blue-600 hover:text-white flex items-center"
                      >
                        Apply &rarr;
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-700">
                  No job openings found for this category
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default Career;
